/**
 * Service tags: normalization, Firestore doc ids, legacy extraction, item defaults parsing.
 */

export function normalizeTagName(raw) {
  return String(raw || '').trim().toLowerCase();
}

function simpleHash(str) {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h).toString(36);
}

/** Stable id for companies/{companyId}/serviceTags/{id} */
export function tagDocIdFromName(raw) {
  const n = normalizeTagName(raw);
  if (!n) return 't_empty';
  const slug = n
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48);
  const h = simpleHash(n);
  return slug ? `t_${slug}_${h}` : `t_${h}`;
}

export function parseDefaultServiceTagsInput(str) {
  return String(str || '')
    .split(/[,;\n]+/)
    .map((s) => normalizeTagName(s))
    .filter(Boolean);
}

/** Tags used historically in bills/rows (no predefined list). */
export const RATE_BASIS_OPTIONS = [
  { value: 'qty', label: 'Line quantity (qty)' },
  { value: 'nos', label: 'Number of pieces (nos)' },
  { value: 'area', label: 'Area (length × height × nos)' },
  { value: 'flat', label: 'Flat per line' },
];

export const RATE_BASIS_UNCONFIGURED = 'unconfigured';

/** Display order for report grouping and filters. */
export const RATE_BASIS_SORT_ORDER = ['qty', 'nos', 'area', 'flat', RATE_BASIS_UNCONFIGURED];

export function getRateBasisLabel(rateBasis) {
  if (!rateBasis || rateBasis === RATE_BASIS_UNCONFIGURED) {
    return 'Not configured';
  }
  const opt = RATE_BASIS_OPTIONS.find((o) => o.value === rateBasis);
  return opt?.label || String(rateBasis);
}

export function getRateBasisShortLabel(rateBasis) {
  if (!rateBasis || rateBasis === RATE_BASIS_UNCONFIGURED) return 'Unconfigured';
  const opt = RATE_BASIS_OPTIONS.find((o) => o.value === rateBasis);
  if (!opt) return String(rateBasis);
  if (rateBasis === 'qty') return 'Per qty';
  if (rateBasis === 'nos') return 'Per nos';
  if (rateBasis === 'area') return 'Per area';
  if (rateBasis === 'flat') return 'Flat / line';
  return opt.label;
}

export function buildServiceTagMetaByName(serviceTagMaster = []) {
  const map = new Map();
  (serviceTagMaster || []).forEach((tag) => {
    const key = normalizeTagName(tag.normalizedName || tag.name);
    if (!key || tag.isActive === false) return;
    map.set(key, tag);
  });
  return map;
}

export function getRowRateBasisQuantity(row, rateBasis = 'qty') {
  if (!row) return 0;
  const nos = parseFloat(row.nos) || 1;
  const qty = parseFloat(row.qty) || 0;
  const length = parseFloat(row.length) || 0;
  const height = parseFloat(row.height) || 0;
  switch (rateBasis) {
    case 'nos':
      return nos;
    case 'area':
      return length * height * nos;
    case 'flat':
      return 1;
    case 'qty':
    default:
      return qty > 0 ? qty : nos;
  }
}

export function calculateInternalCostFromTag(tagMeta, row) {
  const rate = parseFloat(tagMeta?.defaultRate);
  if (!Number.isFinite(rate) || rate <= 0) return null;
  const basis = tagMeta?.rateBasis || 'qty';
  const multiplier = getRowRateBasisQuantity(row, basis);
  return Math.round(rate * multiplier * 100) / 100;
}

/** All normalized names that resolve to this tag document (current + legacy aliases). */
export function getTagAliasNames(tag) {
  const names = new Set();
  if (!tag) return names;
  const primary = normalizeTagName(tag.normalizedName || tag.name);
  if (primary) names.add(primary);
  if (tag.renamedFrom) names.add(normalizeTagName(tag.renamedFrom));
  if (Array.isArray(tag.legacyNames)) {
    tag.legacyNames.forEach((raw) => {
      const a = normalizeTagName(raw);
      if (a) names.add(a);
    });
  }
  return names;
}

export function tagMatchesAlias(tag, rawName) {
  const n = normalizeTagName(rawName);
  if (!n || !tag) return false;
  return getTagAliasNames(tag).has(n);
}

/** Keep old bill/job tag strings valid after a rename. */
export function mergeLegacyNamesForRename(existingTag, oldName, newName) {
  const next = new Set();
  (existingTag?.legacyNames || []).forEach((raw) => {
    const a = normalizeTagName(raw);
    if (a && a !== newName) next.add(a);
  });
  const prev = normalizeTagName(oldName);
  if (prev && prev !== newName) next.add(prev);
  return Array.from(next);
}

export function getTagMatchKey(tag) {
  return normalizeTagName(tag?.normalizedName || tag?.name);
}

/** Prefer configured tags over empty duplicates (same name, different Firestore doc ids). */
function serviceTagCompletenessScore(tag) {
  if (!tag) return -1;
  let score = 0;
  if (tag.isActive !== false) score += 100;
  if (parseFloat(tag.defaultRate) > 0) score += 40;
  if (
    tag.rateBasis &&
    tag.rateBasis !== RATE_BASIS_UNCONFIGURED &&
    tag.rateBasis !== 'unconfigured'
  ) {
    score += 30;
  }
  if (parseFloat(tag.defaultCustomerCharge) > 0) score += 10;
  if (tag.notes) score += 5;
  score += normalizePreferredVendorIds(tag).length * 2;
  if (Array.isArray(tag.legacyNames) && tag.legacyNames.length) score += 3;
  return score;
}

export function findServiceTagsMatchingName(serviceTagMaster, rawName) {
  const n = normalizeTagName(rawName);
  if (!n || !Array.isArray(serviceTagMaster)) return [];
  return serviceTagMaster.filter((t) => getTagMatchKey(t) === n || tagMatchesAlias(t, n));
}

/** Single tag record when multiple Firestore docs share the same name. */
export function resolveCanonicalServiceTag(serviceTagMaster, rawNameOrTag) {
  const list = Array.isArray(serviceTagMaster) ? serviceTagMaster : [];
  const tagObj =
    rawNameOrTag && typeof rawNameOrTag === 'object' ? rawNameOrTag : null;
  const name = tagObj ? getTagMatchKey(tagObj) : normalizeTagName(rawNameOrTag);
  if (!name) return tagObj || null;

  let candidates = findServiceTagsMatchingName(list, name);
  if (tagObj?.id && !candidates.some((t) => t.id === tagObj.id)) {
    candidates = [...candidates, tagObj];
  }
  if (candidates.length === 0) {
    const id = tagDocIdFromName(name);
    return tagObj?.id
      ? { ...tagObj, normalizedName: name, name: tagObj.name || name }
      : { id, normalizedName: name, name, isActive: true };
  }

  const ranked = [...candidates].sort((a, b) => {
    const scoreDiff = serviceTagCompletenessScore(b) - serviceTagCompletenessScore(a);
    if (scoreDiff !== 0) return scoreDiff;
    const canonicalId = tagDocIdFromName(name);
    if (a.id === canonicalId && b.id !== canonicalId) return -1;
    if (b.id === canonicalId && a.id !== canonicalId) return 1;
    return String(a.id).localeCompare(String(b.id));
  });
  return ranked[0];
}

export function findDuplicateTagsForName(serviceTagMaster, rawNameOrTag) {
  const canonical = resolveCanonicalServiceTag(serviceTagMaster, rawNameOrTag);
  const name = getTagMatchKey(canonical) || normalizeTagName(rawNameOrTag);
  if (!name || !canonical?.id) return [];
  return (serviceTagMaster || []).filter(
    (t) =>
      t.id !== canonical.id &&
      t.isActive !== false &&
      (getTagMatchKey(t) === name || tagMatchesAlias(t, name))
  );
}

export function mergeServiceTagFields(primary = {}, secondary = {}) {
  const preferredVendorIds = Array.from(
    new Set([
      ...normalizePreferredVendorIds(primary),
      ...normalizePreferredVendorIds(secondary),
    ])
  );
  const defaultRate =
    parseFloat(primary.defaultRate) > 0
      ? primary.defaultRate
      : parseFloat(secondary.defaultRate) || 0;
  let rateBasis = primary.rateBasis;
  if (!rateBasis || rateBasis === RATE_BASIS_UNCONFIGURED) {
    rateBasis = secondary.rateBasis && secondary.rateBasis !== RATE_BASIS_UNCONFIGURED
      ? secondary.rateBasis
      : 'qty';
  }
  const defaultCustomerCharge =
    parseFloat(primary.defaultCustomerCharge) > 0
      ? primary.defaultCustomerCharge
      : parseFloat(secondary.defaultCustomerCharge) || 0;
  const primaryKey = getTagMatchKey(primary);
  const legacyNames = Array.from(
    new Set([
      ...(Array.isArray(primary.legacyNames) ? primary.legacyNames : []),
      ...(Array.isArray(secondary.legacyNames) ? secondary.legacyNames : []),
      getTagMatchKey(secondary),
    ].map((x) => normalizeTagName(x)).filter((x) => x && x !== primaryKey))
  );

  return {
    ...secondary,
    ...primary,
    defaultRate,
    rateBasis,
    defaultCustomerCharge,
    notes: String(primary.notes || secondary.notes || '').trim(),
    preferredVendorIds,
    legacyNames: legacyNames.length ? legacyNames : primary.legacyNames,
    normalizedName: primaryKey || getTagMatchKey(secondary),
    name: primaryKey || getTagMatchKey(secondary),
    isActive: primary.isActive !== false || secondary.isActive !== false,
  };
}

export function dedupeServiceTagsForDisplay(serviceTagMaster) {
  const list = Array.isArray(serviceTagMaster) ? serviceTagMaster : [];
  const groups = new Map();
  list.forEach((t) => {
    const key = getTagMatchKey(t);
    if (!key) return;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(t);
  });
  const out = [];
  groups.forEach((group, key) => {
    out.push(resolveCanonicalServiceTag(group, key));
  });
  return out.sort((a, b) => getTagMatchKey(a).localeCompare(getTagMatchKey(b)));
}

/** Firestore document id for writes — never create a second doc for the same tag name. */
export function getServiceTagDocId(serviceTagMaster, rawName) {
  const canonical = resolveCanonicalServiceTag(serviceTagMaster, rawName);
  const name = normalizeTagName(rawName);
  return canonical?.id || tagDocIdFromName(name);
}

/**
 * Resolve tag settings for a name used on bills/jobs (current name, legacy alias, or renamed chain).
 */
export function findServiceTagMeta(serviceTagMaster, tagName) {
  const n = normalizeTagName(tagName);
  if (!n || !Array.isArray(serviceTagMaster) || serviceTagMaster.length === 0) return null;

  const activeTags = serviceTagMaster.filter((t) => t.isActive !== false);
  const inactiveTags = serviceTagMaster.filter((t) => t.isActive === false);

  const activeMatches = activeTags.filter((t) => tagMatchesAlias(t, n));
  if (activeMatches.length > 0) {
    return resolveCanonicalServiceTag(activeMatches, n);
  }

  const retired = inactiveTags.find(
    (t) => normalizeTagName(t.normalizedName || t.name) === n && t.renamedTo
  );
  if (retired?.renamedTo) {
    const renamedMatches = activeTags.filter((t) => tagMatchesAlias(t, retired.renamedTo));
    if (renamedMatches.length) {
      return resolveCanonicalServiceTag(renamedMatches, retired.renamedTo);
    }
  }

  const aliasMatches = activeTags.filter((t) => getTagAliasNames(t).has(n));
  if (aliasMatches.length) return resolveCanonicalServiceTag(aliasMatches, n);

  const inactiveMatches = inactiveTags.filter((t) => tagMatchesAlias(t, n));
  if (inactiveMatches.length) return resolveCanonicalServiceTag(inactiveMatches, n);
  return null;
}

export function normalizeVendorServiceTags(raw) {
  if (!Array.isArray(raw)) return [];
  return Array.from(new Set(raw.map((t) => normalizeTagName(t)).filter(Boolean)));
}

export function isPartyServiceVendor(party) {
  if (!party) return false;
  if (party.isServiceVendor === true) return true;
  const pt = party.partyType;
  return pt === 'Seller' || pt === 'Both';
}

/** Parties that can perform a given service tag (pasting, framing, etc.). */
export function getVendorsForServiceTag(parties, tagName, tagMeta = null) {
  const tag = normalizeTagName(tagName);
  if (!tag) return [];
  const matched = (parties || []).filter((p) => {
    if (!isPartyServiceVendor(p)) return false;
    const tags = normalizeVendorServiceTags(p.vendorServiceTags);
    if (tags.length === 0) return true;
    return tags.includes(tag);
  });
  const byId = new Map(matched.map((p) => [p.id, p]));
  normalizePreferredVendorIds(tagMeta).forEach((id) => {
    const p = (parties || []).find((x) => x.id === id);
    if (p && isPartyServiceVendor(p) && !byId.has(p.id)) {
      byId.set(p.id, p);
    }
  });
  return sortVendorsForTag(Array.from(byId.values()), tagMeta);
}

/** Ordered favorite/default vendor ids (supports legacy single preferredVendorId). */
export function normalizePreferredVendorIds(tagMeta) {
  if (!tagMeta) return [];
  if (Array.isArray(tagMeta.preferredVendorIds) && tagMeta.preferredVendorIds.length > 0) {
    return Array.from(new Set(tagMeta.preferredVendorIds.map((id) => String(id)).filter(Boolean)));
  }
  if (tagMeta.preferredVendorId) return [String(tagMeta.preferredVendorId)];
  return [];
}

export function isPreferredVendorForTag(tagMeta, partyId) {
  if (!partyId) return false;
  return normalizePreferredVendorIds(tagMeta).includes(String(partyId));
}

export function buildPreferredVendorPatch(partyIds, parties = []) {
  const ids = Array.from(new Set((partyIds || []).map((id) => String(id)).filter(Boolean)));
  const names = ids
    .map((id) => {
      const p = (parties || []).find((x) => x.id === id);
      return p ? String(p.firmName || p.name || '').trim() : '';
    })
    .filter(Boolean);
  return {
    preferredVendorIds: ids,
    preferredVendorId: ids[0] || '',
    preferredVendorName: names[0] || '',
    preferredVendorNames: names,
  };
}

export function promotePreferredVendorId(currentIds, partyId) {
  const id = String(partyId || '');
  if (!id) return currentIds || [];
  const rest = (currentIds || []).filter((x) => x !== id);
  return [id, ...rest];
}

export function togglePreferredVendorId(currentIds, partyId) {
  const id = String(partyId || '');
  if (!id) return currentIds || [];
  const list = currentIds || [];
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}

/** Preferred vendors first (in list order), then alphabetical. */
export function sortVendorsForTag(vendors, tagMeta = null) {
  const preferredIds = normalizePreferredVendorIds(tagMeta);
  const list = [...(vendors || [])];
  if (!preferredIds.length) {
    return list.sort((a, b) =>
      String(a.firmName || a.name || '').localeCompare(String(b.firmName || b.name || ''))
    );
  }
  list.sort((a, b) => {
    const aIdx = preferredIds.indexOf(a.id);
    const bIdx = preferredIds.indexOf(b.id);
    const aRank = aIdx >= 0 ? aIdx : 9999;
    const bRank = bIdx >= 0 ? bIdx : 9999;
    if (aRank !== bRank) return aRank - bRank;
    return String(a.firmName || a.name || '').localeCompare(String(b.firmName || b.name || ''));
  });
  return list;
}

function isPartyValidForTag(party, tagName) {
  if (!party || !isPartyServiceVendor(party)) return false;
  const tag = normalizeTagName(tagName);
  const tags = normalizeVendorServiceTags(party.vendorServiceTags);
  if (tags.length > 0 && tag && !tags.includes(tag)) return false;
  return true;
}

export function resolvePreferredVendors(parties, tagMeta) {
  return normalizePreferredVendorIds(tagMeta)
    .map((id) => (parties || []).find((p) => p.id === id))
    .filter((p) => p && isPartyServiceVendor(p));
}

/** Apply primary favorite vendor (and optional cost) to a service job row in memory. */
export function enrichJobWithSuggestedVendor(job, parties, serviceTagMaster) {
  if (!job || job.vendorId) return job;
  const tagMeta = findServiceTagMeta(serviceTagMaster, job.serviceName);
  const suggested = suggestVendorForTag(parties, job.serviceName, tagMeta);
  if (!suggested) return job;
  const rowLike = {
    qty: job.qty,
    nos: job.nos,
    length: job.length,
    height: job.height,
  };
  let internalCost = job.internalCost;
  const costNum = parseFloat(internalCost);
  if ((!Number.isFinite(costNum) || costNum <= 0) && tagMeta) {
    const computed = calculateInternalCostFromTag(tagMeta, rowLike);
    if (computed != null) internalCost = computed;
  }
  return {
    ...job,
    vendorId: suggested.id,
    vendorName: suggested.firmName || suggested.name || '',
    internalCost,
  };
}

export function enrichJobsWithSuggestedVendors(jobs, parties, serviceTagMaster) {
  return (jobs || []).map((job) => enrichJobWithSuggestedVendor(job, parties, serviceTagMaster));
}

export function resolvePreferredVendor(parties, tagMeta) {
  const list = resolvePreferredVendors(parties, tagMeta);
  return list[0] || null;
}

export function suggestVendorForTag(parties, tagName, tagMeta = null) {
  const name = normalizeTagName(tagName);
  const preferred = resolvePreferredVendor(parties, tagMeta);
  if (preferred) return preferred;
  const vendors = getVendorsForServiceTag(parties, name, tagMeta);
  if (vendors.length === 1) return vendors[0];
  return null;
}

export function buildServiceEntryForTag({
  serviceName,
  row,
  serviceTagMaster,
  parties,
  existingEntry = null,
}) {
  const name = normalizeTagName(serviceName);
  const tagMeta = findServiceTagMeta(serviceTagMaster, name);
  const base = existingEntry || {
    serviceName: name,
    vendorId: '',
    vendorName: '',
    internalCost: '',
    customerCharge: '',
    showOnInvoice: false,
    status: 'pending',
  };
  const hasInternalCost =
    base.internalCost !== '' && base.internalCost !== null && base.internalCost !== undefined;
  let internalCost = base.internalCost;
  if (!hasInternalCost && tagMeta && row) {
    const computed = calculateInternalCostFromTag(tagMeta, row);
    if (computed != null) internalCost = computed;
  }
  let vendorId = base.vendorId || '';
  let vendorName = base.vendorName || '';
  if (!vendorId) {
    const suggested = suggestVendorForTag(parties, name, tagMeta);
    if (suggested) {
      vendorId = suggested.id;
      vendorName = suggested.firmName || suggested.name || '';
    }
  }
  let customerCharge = base.customerCharge;
  const hasCustomerCharge =
    customerCharge !== '' && customerCharge !== null && customerCharge !== undefined;
  if (!hasCustomerCharge && tagMeta?.defaultCustomerCharge != null) {
    const dc = parseFloat(tagMeta.defaultCustomerCharge);
    if (Number.isFinite(dc) && dc > 0) customerCharge = dc;
  }
  return {
    ...base,
    serviceName: name,
    vendorId,
    vendorName,
    internalCost,
    customerCharge,
  };
}

export function collectLegacyTagsFromRowsAndBills(rows = [], bills = []) {
  const tagSet = new Set();
  rows.forEach((row) => {
    (row.tags || []).forEach((tag) => {
      const normalized = normalizeTagName(tag);
      if (normalized) tagSet.add(normalized);
    });
    (row.services || []).forEach((service) => {
      const normalized = normalizeTagName(service?.serviceName);
      if (normalized) tagSet.add(normalized);
    });
  });
  bills.forEach((bill) => {
    (bill.tagSummary || []).forEach((tag) => {
      const normalized = normalizeTagName(tag);
      if (normalized) tagSet.add(normalized);
    });
    (bill.rows || []).forEach((row) => {
      (row.tags || []).forEach((tag) => {
        const normalized = normalizeTagName(tag);
        if (normalized) tagSet.add(normalized);
      });
      (row.services || []).forEach((service) => {
        const normalized = normalizeTagName(service?.serviceName);
        if (normalized) tagSet.add(normalized);
      });
    });
  });
  return tagSet;
}
